import { format, parse } from 'date-fns'
import { useMemo } from 'react'

import { FixSessionMessageDto } from '@/gen/publicApi'

import { Badge } from '@/components/badge'
import OrderSideText from '@/components/orderSideText'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table'

import { FixMessagePartDetails, FixSpecSides, getMessagePartDetails, isAdmin, splitMessageIntoParts } from '@/utils/fix'

const formattedDetail = (part: FixMessagePartDetails) => {
    switch (part.format) {
        case 'timestamp':
            // Example 20240603-07:10:20.820
            // eslint-disable-next-line no-case-declarations
            const parsedString = parse(part.value, 'yyyyLLdd-HH:mm:ss.SSS', new Date())
            return format(parsedString, 'dd/LL/yyyy HH:mm:ss.SSS')
        default:
            return part.value
    }
}

interface MessageTableProps {
    messages: FixSessionMessageDto[]
}

export default function OrderMessageTable({ messages }: MessageTableProps) {
    messages = messages.filter(message => !message.isAdminMsg)
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Direction</TableHead>
                    <TableHead>MsgType</TableHead>
                    <TableHead>OrdType</TableHead>
                    <TableHead>OrdStatus</TableHead>
                    <TableHead>ClOrdId</TableHead>
                    <TableHead>Side</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>OrderQty</TableHead>
                    <TableHead>LastPx</TableHead>
                    <TableHead>LastQty</TableHead>
                    <TableHead>Timestamp</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {messages.map(message => (
                    <OrderMessageTableRow key={message.id} message={message} />
                ))}
            </TableBody>
        </Table>
    )
}

interface OrderMessageTableRowProps {
    message: FixSessionMessageDto
}

function OrderMessageTableRow({ message }: OrderMessageTableRowProps) {
    const messageParts = useMemo(() => splitMessageIntoParts(message.message), [message.message])
    const messageType = messageParts.find(part => part.tag == 35)
    const messageTypeDetails = getMessagePartDetails(messageType)
    const isAdminMsg = isAdmin(messageType?.value)

    return (
        <TableRow>
            <TableCell>
                <Badge variant={message.direction == 'In' ? 'secondary' : 'outline'}>{message.direction}</Badge>
            </TableCell>
            <TableCell>
                {messageTypeDetails?.description && (
                    <Badge variant={isAdminMsg ? 'outline' : 'default'}>{messageTypeDetails?.description}</Badge>
                )}
            </TableCell>
            <TableCell>{getMessagePartDetails(messageParts.find(part => part.tag == 40))?.description}</TableCell>
            <TableCell>{getMessagePartDetails(messageParts.find(part => part.tag == 39))?.description}</TableCell>
            <TableCell>
                {messageParts.find(part => part.tag == 11)?.value}
                {messageParts.find(part => part.tag == 41)?.value &&
                    ` (${messageParts.find(part => part.tag == 41)?.value})`}
            </TableCell>
            <TableCell>
                <OrderSideText
                    side={getMessagePartDetails(messageParts.find(part => part.tag == 54))?.description as FixSpecSides}
                />
            </TableCell>
            <TableCell>{messageParts.find(part => part.tag == 55)?.value}</TableCell>
            <TableCell>{messageParts.find(part => part.tag == 44)?.value}</TableCell>
            <TableCell>{messageParts.find(part => part.tag == 38)?.value}</TableCell>
            <TableCell>{messageParts.find(part => part.tag == 31)?.value}</TableCell>
            <TableCell>{messageParts.find(part => part.tag == 32)?.value}</TableCell>
            <TableCell>{formattedDetail(getMessagePartDetails(messageParts.find(part => part.tag == 52))!)}</TableCell>
        </TableRow>
    )
}
